import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Loader2, X, Zap } from "lucide-react";
import { api, fmtPrice } from "../lib/api";
import { useCart } from "../context/CartContext";

const EarlyAccessSignup = ({ sessionId }) => {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle, sending, done, error

  const submit = async (e) => {
    e.preventDefault();
    setState("sending");
    try {
      await api.post("/early-access", { email, session_id: sessionId });
      setState("done");
    } catch { setState("error"); }
  };

  return (
    <div data-testid="early-access-box" className="mt-12 border border-[#0B0E14] p-6 sm:p-8 text-left">
      <div className="flex items-center gap-2 mb-2">
        <Zap className="w-4 h-4" strokeWidth={1.5} />
        <p className="label">Early Access</p>
      </div>
      {state === "done" ? (
        <p data-testid="early-access-success" className="font-serif-display text-2xl mt-2">You're on the list. You'll hear about the next drop first.</p>
      ) : (
        <>
          <p className="text-[#3A4150]">You bought a piece — that puts you first in line. Drop your email to get early access to the next drop before anyone else.</p>
          <form onSubmit={submit} className="mt-5 flex flex-col sm:flex-row gap-3">
            <input
              type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              data-testid="early-access-email"
              placeholder="you@email.com"
              className="flex-1 h-12 px-4 bg-transparent border border-[#0B0E14] outline-none focus:ring-1 focus:ring-[#0B0E14]"
            />
            <button
              data-testid="early-access-submit" disabled={state === "sending"}
              className="bg-[#0B0E14] text-[#EDEEF1] px-8 h-12 label hover:bg-[#1B2233] transition-colors disabled:opacity-50"
            >
              {state === "sending" ? "Signing up…" : "Sign Me Up"}
            </button>
          </form>
          {state === "error" && <p data-testid="early-access-error" className="text-red-700 text-sm mt-3">That didn't go through — check the email and try again.</p>}
        </>
      )}
    </div>
  );
};

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const [status, setStatus] = useState("checking"); // checking, paid, failed
  const [data, setData] = useState(null);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setData(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          if (!cleared.current) { clear(); cleared.current = true; }
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) { setStatus("failed"); return; }
      } catch (err) {
        console.error("Payment status poll failed:", err?.response?.status || err?.message || err);
      }
      if (attempts++ < 6) setTimeout(poll, 2000);
      else setStatus("failed");
    };
    poll();
    // eslint-disable-next-line
  }, [sessionId]);

  return (
    <div data-testid="payment-success-page" className="min-h-screen flex items-center justify-center px-6 pt-20">
      <motion.div
        initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
        className="max-w-lg w-full text-center"
      >
        {status === "checking" && (
          <>
            <Loader2 className="w-10 h-10 mx-auto animate-spin mb-8" strokeWidth={1} />
            <p className="font-serif-display text-3xl">Confirming your order…</p>
            <p className="label text-[#8A8F99] mt-3">One moment</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="w-16 h-16 mx-auto border border-[#0B0E14] flex items-center justify-center mb-8">
              <Check className="w-7 h-7" strokeWidth={1.2} />
            </div>
            <p className="label text-[#8A8F99] mb-4">Order Confirmed</p>
            <h1 className="font-serif-display text-5xl md:text-6xl tracking-tight leading-none">Thank you</h1>
            <p className="text-[#3A4150] mt-6 text-lg">Your pieces are being prepared with care. A confirmation is on its way.</p>
            {data?.amount != null && <p className="mt-6 text-xl">{fmtPrice(data.amount)} · {(data.items || []).join(", ")}</p>}
            <EarlyAccessSignup sessionId={sessionId} />
            <Link to="/shop" data-testid="continue-shopping" className="mt-10 inline-flex bg-[#0B0E14] text-[#EDEEF1] px-10 h-14 items-center label hover:bg-[#1B2233] transition-colors">
              Continue Browsing
            </Link>
          </>
        )}
        {status === "failed" && (
          <>
            <div className="w-16 h-16 mx-auto border border-[#0B0E14] flex items-center justify-center mb-8">
              <X className="w-7 h-7" strokeWidth={1.2} />
            </div>
            <h1 className="font-serif-display text-4xl">We couldn't confirm payment</h1>
            <p className="text-[#3A4150] mt-4">If you were charged, your order is safe and we'll reach out. Otherwise, please try again.</p>
            <Link to="/shop" className="mt-8 inline-flex label link-underline">Return to collection</Link>
          </>
        )}
      </motion.div>
    </div>
  );
}
